/* The gap tile — `Builder planu sekwencja.dc.html`'s `s.gapSt` and the three parts inside it:
 *
 *   flex:1; display:flex; flex-direction:column; align-items:center; justify-content:center;
 *   gap:8px; border-radius:14px; border:1px dashed var(--border-2);
 *   background:transparent; opacity: c > 0 ? 1 : .7; transition: opacity .2s ease
 *
 * The column's WIDTH and its head are `TapePane`'s — the mock sets the flex basis on the column, not
 * on the tile, and draws the same head over every kind of column.
 *
 * ── The dashed edge is `--border-2`, and the note it used to carry is withdrawn ──────────────
 * It was `--border-3`, on the argument that this dashed edge is not a frame around content but the
 * CODE that says "gap, not training", which WCAG 1.4.11 asks 3:1 of. Half of that argument is gone:
 * the legend that spelled the code out went with this rebuild, because the tile now writes "PRZERWA"
 * and "bez przerwy" in words and the code is no longer carried by shape alone. The mock's own value
 * is `--border-2` and that is what ships. Recorded rather than smuggled: `--border-2` measures
 * 1.31:1 light, and nothing here is told apart by it.
 *
 * ── `--text-3` IS used for text here ─────────────────────────────────────────────────────────
 * This file used to open with a refusal of KANON's tertiary ink on contrast grounds. The owner
 * rejected that result beside the week's mock on 8 September 2026 and the same ruling governs here:
 * the mock's inks ship. "PRZERWA" is `--text-3`, and so is "bez przerwy". The shortfall is real and
 * is the designer's to answer.
 *
 * ── The zero-state fade is on the PARTS, and never on the reason ─────────────────────────────
 * The mock fades the whole tile at a gap of zero. This file's own header used to claim the refusal
 * was drawn "in full `--text-2`" — which was not true, because the paragraph inherited the tile's
 * `.7` on top of ink that is already tertiary. Measured in Flame light: "PRZERWA" and "bez przerwy"
 * at 1.86:1, the refusal paragraph at 3.02:1, and the refused `−` glyph at 1.41:1. So the fade sits
 * on `.label`, `.value` and `.stepper` — the parts the mock is actually receding — and `.reason`
 * stands at full `--text-2`, which is the one sentence on this tile a trainer has to read and the
 * only one that explains why a control did nothing. */

/* THREE ROWS, not a centred flex column, and the middle one is the whole point.
 *
 * The mock centres the tile's contents, which is what a flex column with `justify-content: center`
 * does — and it also means a paragraph appearing among them pushes everything above it up by half
 * its height. Measured: with a gap of one day the ± pair sat at y=497; one press of − dropped the
 * gap to zero, the refusal appeared and the pair landed at y=450, 47px out from under a stationary
 * pointer (`elementFromPoint` at that spot was the button before the click and `.label` 600ms
 * after), so a trainer setting a three-day break pressed + twice and got one day.
 *
 * `minmax(0, 1fr) auto minmax(0, 1fr)` centres the CLUSTER against the tile and nothing else: both
 * fr tracks take an equal share whatever stands in them, because their minimum is 0 rather than
 * their content. The refusal lives in the third track, directly under the stepper it explains, and
 * its height cannot reach the cluster. The mock draws no refusal element at all, so this departs
 * from nothing it draws — and `CLAUDE.md`'s "a box knows its size before its data arrives" outranks
 * a static mock on anything that moves. */
.tile {
  display: grid;
  flex: 1;
  grid-template-rows: minmax(0, 1fr) auto minmax(0, 1fr);
  justify-items: center;

  /* The reason and the value need room to wrap inside an 84–108px column rather than widening it. */
  padding: var(--space-2) var(--space-6px);
  background: transparent;
  border: var(--border-hairline) dashed var(--border-2);
  border-radius: var(--radius-md);
}

/* The mock's own centred stack — the word, the figure and the ± pair. */
.cluster {
  display: flex;
  grid-row: 2;
  flex-direction: column;
  gap: var(--space-2);
  align-items: center;
}

/* A gap of ZERO — two trainings on consecutive days. The tile is still drawn, because nothing else
 * on this screen writes `restDaysAfter` and a trainer who shortened a gap to nothing would never get
 * it back (see `toTapeViewModel`). The mock recedes it to `.7` rather than changing its edge, and
 * the words inside it already say "bez przerwy", so nothing is carried by the fade. The rule itself
 * is declared BELOW, after the three parts it fades, so it does not descend in specificity over
 * them. */

.label {
  font-size: var(--fs-pl-micro);
  font-weight: var(--fw-extrabold);
  letter-spacing: var(--ls-head);
  text-transform: uppercase;
  color: var(--text-3);
}

/* An `<output>`, so a press announces — see the component. `display: block` because the element is
 * inline by default and the mock centres it as its own line. */
.value {
  display: block;
  font-size: var(--fs-body);
  font-weight: var(--fw-extrabold);
  letter-spacing: var(--ls-title);
  color: var(--text);
  text-align: center;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

/* The zero-state fade, on the three PARTS the mock recedes rather than on the tile — so the refusal
 * paragraph beside them keeps its full ink. See the header. */
.label,
.value,
.stepper {
  transition-property: opacity;
  transition-duration: var(--dur-base);
  transition-timing-function: var(--ease-default);
}

/* "bez przerwy" — smaller and tertiary, which is the mock's own pair of ternaries on this one span
 * (`fontSize: c > 0 ? '14px' : '11px'`, `color: c > 0 ? '--text' : '--text-3'`). It is a phrase
 * rather than a figure, so it is not tabular and not set at the figure's weight. */
.tile[data-zero] .value {
  font-size: var(--fs-caption);
  color: var(--text-3);
}

/* The mock's pair: two separate 24×24 chips 4px apart, each on `--bg-chip` under a `--border-2`
 * hairline at radius 8. The 04.08 canon shared one edge between them; this one does not. */
.stepper {
  display: inline-flex;
  flex: none;
  gap: var(--space-1);
}

.tile[data-zero] .label,
.tile[data-zero] .value,
.tile[data-zero] .stepper {
  opacity: 0.7;
}

/* 24×24 clears WCAG 2.2 SC 2.5.8 on its own, so unlike the 22px cells this replaces there is no
 * `--hit-extend` pseudo-element growing an invisible target. */
.step {
  display: grid;
  flex: none;
  place-items: center;
  width: var(--h-gap-stepper);
  height: var(--h-gap-stepper);
  padding: var(--space-0);
  font-family: var(--font-body);
  font-size: var(--fs-step-glyph);
  font-weight: var(--fw-bold);
  line-height: var(--lh-none);
  color: var(--text-2);
  user-select: none;
  cursor: pointer;
  background: var(--bg-chip);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-8px);
}

.step:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.step:hover:not([aria-disabled='true']) {
  color: var(--text);
}

/* Dimmed to the mock's own `.35`, focusable, and carrying its reason — never the `disabled`
 * attribute, which would drop the control out of the tab order and take the explanation with it
 * (view ADR §12). The dead cursor and the drawn reason are what carry the state; the opacity step
 * is not on its own. */
.step[aria-disabled='true'] {
  cursor: default;
  opacity: 0.35;
}

/* DRAWN, not hidden behind `title`: this is the one place the gap's bounds and a frozen phase are
 * ever stated on this canvas, and a `title` reaches neither a touch user nor a keyboard user who
 * never hovers. It stands inside the column, so it wraps rather than widening the track.
 *
 * Its slot is the tile's third track and is RESERVED whether or not a refusal exists — see `.tile`
 * for the measurement that made that mandatory. `align-self: start` keeps it directly under the
 * stepper it explains rather than floating in the middle of the space below. */
.reason {
  grid-row: 3;
  align-self: start;
  margin: var(--space-2) var(--space-0) var(--space-0);
  font-size: var(--fs-tag);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-hint-dense);
  color: var(--text-2);
  text-align: center;
}
